<template>
    <div class="dt-actions" v-if="actions.length">
        <button class="btn btn-primary" v-for="action in actions" @click="$emit('action', action)">
            <i class="bi" v-show="action?.icon" :class="action?.icon"></i>
            {{ action.text }}
        </button>
    </div>
    <div class="dt-card">
        <div class="table-responsive">
            <table class="table mb-0">
                <thead>
                    <tr>
                        <th v-if="counter" class="dt-counter">#</th>
                        <th v-for="col in columns" :key="col.key">{{ col.value }}</th>
                        <th v-if="rows.some(f => f.actions)"></th>
                    </tr>
                </thead>
                <tbody v-if="rows.length">
                    <tr v-for="(row, index) in rows" :key="row.id || index" @click="$emit('row', { type: 'clicked', item: row })">
                        <td v-if="counter" class="dt-counter">{{ index + 1 }}</td>
                        <td v-for="col in columns" :key="col.key">
                            <span v-if="col.badge" class="dt-badge" :class="col.badgeClass?.(row)">{{ row[col?.key] ?? '' }}</span>
                            <template v-else>{{ row[col?.key] ?? '' }}</template>
                        </td>
                        <td v-if="rows.some(p => p.actions)">
                            <router-link v-for="action in row.actions" :to="action.action" @click.stop class="btn btn-sm btn-light action-btn"><i :class="action.icon"></i></router-link>
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr>
                        <td :colspan="columns.length + (counter ? 1 : 0)" class="dt-empty">
                            <i class="bi bi-inbox"></i>
                            <p>{{ $t('dataTable.empty') }}</p>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
    <div v-if="total" class="dt-footer">
        <span class="dt-info">
            {{ $t('dataTable.range', { from: Math.min((currentPage - 1) * perPage + 1, total), to: Math.min(currentPage * perPage, total), total }) }}
        </span>
        <nav>
            <ul class="pagination mb-0">
                <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                    <a class="page-link" href="#" @click.prevent="goToPage(1)"><i class="bi bi-chevron-double-left"></i></a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                    <a class="page-link" href="#" @click.prevent="goToPage(prevPage)"><i class="bi bi-chevron-left"></i></a>
                </li>
                <li v-for="p in shownPages" :key="p" class="page-item" :class="{ active: p == currentPage }">
                    <a class="page-link" href="#" @click.prevent="goToPage(p)">{{ p }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                    <a class="page-link" href="#" @click.prevent="goToPage(nextPage)"><i class="bi bi-chevron-right"></i></a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                    <a class="page-link" href="#" @click.prevent="goToPage(totalPages)"><i class="bi bi-chevron-double-right"></i></a>
                </li>
            </ul>
        </nav>
    </div>
</template>

<script>
import { opp } from '../../lib/utils'

export default {
    props: {
        actions: { type: Array, default: () => [] },
        columns: { type: Array, default: () => [] },
        rows: { type: Array, default: () => [] },
        counter: { type: Boolean, default: false },
        total: { type: Number, default: 0 },
        totalPages: { type: Number, default: 1 },
        currentPage: { type: Number, default: 1 },
        perPage: { type: Number, default: opp }
    },
    computed: {
        shownPages() {
            if(this.totalPages <= 3) {
                return Array.from({ length: this.totalPages }, (_, i) => i + 1)
            }
            if(this.currentPage === 1) return [1, 2, 3]
            if(this.currentPage === this.totalPages) return [this.totalPages - 2, this.totalPages - 1, this.totalPages]
            return [this.currentPage - 1, this.currentPage, this.currentPage + 1]
        },
        nextPage() { return this.currentPage + 1 },
        prevPage() { return this.currentPage - 1 }
    },
    methods: {
        goToPage(page) {
            if(page < 1 || page > this.totalPages) return
            this.$emit('goToPage', page)
        }
    }
}
</script>

<style scoped>
.dt-actions {
    margin-bottom: 1em;
}

.dt-card {
    background: var(--bg-card);
    border-radius: var(--border-radius);
    box-shadow: var(--shadow-sm);
    overflow: hidden;
}

.table {
    font-size: 13px;
}

.table thead th {
    background-color: #f8f9fb;
    border-bottom: 1px solid var(--border-color);
    padding: 0.75em 1em;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-secondary);
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 1;
}

.table tbody tr {
    cursor: pointer;
    transition: var(--default-transition);
}

.table tbody tr:hover {
    background-color: rgba(var(--primary-color-rgb), 0.03);
}

.table tbody tr:not(:last-child) td {
    border-bottom: 1px solid #f3f4f6;
}

.table tbody tr:last-child td {
    border-bottom: 0;
}

.table tbody td {
    padding: 0.7em 1em;
    vertical-align: middle;
    color: var(--text-primary);
}

.dt-counter {
    width: 50px;
    color: var(--text-muted);
}

.dt-empty {
    text-align: center;
    padding: 3em 1em !important;
    cursor: default !important;
}

.dt-empty .bi {
    font-size: 2.5em;
    color: var(--text-muted);
    display: block;
    margin-bottom: 0.25em;
}

.dt-empty p {
    color: var(--text-muted);
    margin: 0;
    font-size: 13px;
}

.action-btn {
    min-width: auto !important;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.25em;
}

/* ── Badges ── */
.dt-badge {
    display: inline-block;
    padding: 0.2em 0.65em;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    white-space: nowrap;
}

.dt-badge.badge-success {
    background-color: #dcfce7;
    color: #16a34a;
}

.dt-badge.badge-warning {
    background-color: #fef3c7;
    color: #d97706;
}

/* ── Footer / Pagination ── */
.dt-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1em 0;
}

.dt-info {
    font-size: 13px;
    color: var(--text-secondary);
}

.pagination .page-item .page-link {
    border: 0;
    border-radius: 8px;
    color: var(--text-secondary);
    font-size: 13px;
    padding: 0.35em 0.7em;
    margin: 0 1px;
    transition: var(--default-transition);
}

.pagination .page-item .page-link:hover {
    background-color: var(--bg-page);
    color: var(--primary-color);
}

.pagination .page-item .page-link:focus {
    box-shadow: none;
}

.pagination .page-item.active .page-link {
    background-image: var(--main-gradient);
    color: #fff;
}

.pagination .page-item.disabled .page-link {
    opacity: 0.4;
}
</style>
